import {
  isSupportedLanguage,
  type SupportedLanguage,
  type UiStringKey,
} from './ui-strings';

export type AgentLanguageSurface = 'website' | 'demo' | 'onboarding';

export interface LanguageOption {
  code: SupportedLanguage;
  label: string;
  nativeLabel: string;
  uiStrings: Partial<Record<UiStringKey, string>>;
}

export interface LocalizationPolicy {
  defaultLanguage: SupportedLanguage;
  languages: LanguageOption[];
  midSessionSwitch: boolean;
}

export interface LocalizationPolicies {
  website?: unknown;
  demo?: unknown;
  onboarding?: unknown;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PendingLanguageSwitch {
  language: SupportedLanguage;
  requestId: string;
}

export interface LanguageSwitchState {
  pending: PendingLanguageSwitch | null;
  error: string | null;
  /**
   * A request whose UI wait timed out. It is kept only long enough to reconcile
   * a late acknowledgement for that exact request; acknowledgements for older
   * or unrelated requests remain stale and are ignored.
   */
  recoverableRequestId?: string;
}

export interface LanguageChangedPayload {
  language?: unknown;
  requestId?: unknown;
  source?: unknown;
}

export interface LanguageChangeFailedPayload {
  language?: unknown;
  requestId?: unknown;
  source?: unknown;
}

export interface LanguageSwitchResult {
  state: LanguageSwitchState;
  commitLanguage?: SupportedLanguage;
}

export type LanguagePreferenceSource =
  | 'initial'
  | 'ui'
  | 'user'
  | 'spoken_request'
  | 'detected'
  | 'unknown';

/**
 * Existing dashboard copy has no language tag. Its ownership is therefore
 * explicit here: it is English-authored and may only render in English. A
 * non-English surface must use its localized string pack or generated copy.
 */
export function resolveUntaggedOperatorCopy<T>(
  value: T | null | undefined,
  language: SupportedLanguage,
): T | undefined {
  return language === 'en' && value != null ? value : undefined;
}

/** Passive auto-detection is session state, not a durable visitor preference. */
export function shouldPersistLanguageSource(source: LanguagePreferenceSource): boolean {
  return source === 'ui' || source === 'user' || source === 'spoken_request';
}

/** Resolve wire metadata without allowing arbitrary source strings to persist. */
export function languagePreferenceSource(
  payload: LanguageChangedPayload,
  pending: PendingLanguageSwitch | null,
): LanguagePreferenceSource {
  if (payload.source === 'detected') return 'detected';
  if (payload.source === 'spoken_request') return 'spoken_request';
  if (payload.source === 'user') return 'user';

  const requestId = typeof payload.requestId === 'string' ? payload.requestId : undefined;
  if (pending && requestId === pending.requestId) return 'ui';
  return 'unknown';
}

export const FIXED_ENGLISH_POLICY: LocalizationPolicy = {
  defaultLanguage: 'en',
  languages: [
    {
      code: 'en',
      label: 'English',
      nativeLabel: 'English',
      uiStrings: {},
    },
  ],
  midSessionSwitch: false,
};

/**
 * A validation response is authoritative configuration, not a compatibility
 * hint. Website/demo callers must stop when that contract is incomplete rather
 * than quietly launching an English or partly configured agent.
 */
export class LocalizationPolicyError extends Error {
  constructor(message: string) {
    super(`[OnboardingSDK] Invalid localization policy: ${message}`);
    this.name = 'LocalizationPolicyError';
  }
}

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return !!raw && typeof raw === 'object' && !Array.isArray(raw);
}

function hasOwn(value: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function invalidPolicy(message: string): never {
  throw new LocalizationPolicyError(message);
}

function parseUiStrings(raw: unknown, optionIndex: number): LanguageOption['uiStrings'] {
  if (!isRecord(raw)) {
    invalidPolicy(`languages[${optionIndex}].uiStrings must be an object.`);
  }

  const strings: Partial<Record<UiStringKey, string>> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value !== 'string' || !value.trim()) {
      invalidPolicy(
        `languages[${optionIndex}].uiStrings[${JSON.stringify(key)}] must be a non-blank string.`,
      );
    }
    strings[key as UiStringKey] = value;
  }
  return strings;
}

/**
 * Treat localization policy as untrusted, authoritative network data. Every
 * required field is checked and no value is synthesized: silently dropping a
 * bad language or selecting English/first-enabled would let website/demo start
 * with a configuration different from the operator's saved policy.
 */
export function parseLocalizationPolicy(raw: unknown): LocalizationPolicy {
  if (!isRecord(raw)) {
    invalidPolicy('policy must be an object.');
  }

  const value = raw;
  if (!hasOwn(value, 'languages') || !Array.isArray(value.languages)) {
    invalidPolicy('languages must be a non-empty array.');
  }
  if (value.languages.length === 0) {
    invalidPolicy('languages must contain at least one enabled language.');
  }
  if (!hasOwn(value, 'defaultLanguage') || !isSupportedLanguage(value.defaultLanguage)) {
    invalidPolicy(
      `defaultLanguage ${JSON.stringify(value.defaultLanguage)} is missing or unsupported.`,
    );
  }
  if (!hasOwn(value, 'midSessionSwitch') || typeof value.midSessionSwitch !== 'boolean') {
    invalidPolicy('midSessionSwitch must be a boolean.');
  }

  const seen = new Set<SupportedLanguage>();
  const languages: LanguageOption[] = [];

  for (const [index, candidate] of value.languages.entries()) {
    if (!isRecord(candidate)) {
      invalidPolicy(`languages[${index}] must be an object.`);
    }
    const option = candidate;
    if (!hasOwn(option, 'code') || !isSupportedLanguage(option.code)) {
      invalidPolicy(`languages[${index}].code is missing or unsupported.`);
    }
    if (seen.has(option.code)) {
      invalidPolicy(`language code ${JSON.stringify(option.code)} is duplicated.`);
    }

    const label = hasOwn(option, 'label') && typeof option.label === 'string'
      ? option.label.trim()
      : '';
    const nativeLabel = hasOwn(option, 'nativeLabel') && typeof option.nativeLabel === 'string'
      ? option.nativeLabel.trim()
      : '';
    if (!label) {
      invalidPolicy(`languages[${index}].label must be a non-blank string.`);
    }
    if (!nativeLabel) {
      invalidPolicy(`languages[${index}].nativeLabel must be a non-blank string.`);
    }

    seen.add(option.code);
    languages.push({
      code: option.code,
      label,
      nativeLabel,
      uiStrings: parseUiStrings(option.uiStrings, index),
    });
  }

  const defaultLanguage = value.defaultLanguage;
  if (!seen.has(defaultLanguage)) {
    invalidPolicy(`defaultLanguage ${JSON.stringify(defaultLanguage)} is not enabled.`);
  }

  return {
    defaultLanguage,
    languages,
    midSessionSwitch: value.midSessionSwitch,
  };
}

/** Onboarding is the sole deliberately fixed-English surface. */
export function resolveSurfacePolicy(
  policies: unknown,
  surface: AgentLanguageSurface,
): LocalizationPolicy {
  if (surface === 'onboarding') return FIXED_ENGLISH_POLICY;
  if (!isRecord(policies)) {
    invalidPolicy(`localizationPolicies must contain a ${surface} policy.`);
  }
  if (!hasOwn(policies, surface)) {
    invalidPolicy(`${surface} policy is missing.`);
  }
  return parseLocalizationPolicy(policies[surface]);
}

export function languagePreferenceKey(siteId: unknown, clientKey: string): string {
  const scope = typeof siteId === 'string' && siteId.trim() ? siteId.trim() : clientKey;
  return `floe:language:${encodeURIComponent(scope)}`;
}

function safeRead(storage: StorageLike | undefined, key: string): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeRemove(storage: StorageLike | undefined, key: string): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // Storage may be disabled (private browsing / embedded third-party policy).
  }
}

/**
 * Browser language subtags that name a catalog language under another code.
 * Norwegian browsers report Bokmål (`nb`) or Nynorsk (`nn`), almost never the
 * `no` the catalog uses, so without this a Norwegian visitor would never start
 * in Norwegian.
 */
const BROWSER_SUBTAG_ALIASES: ReadonlyMap<string, string> = new Map([
  ['nb', 'no'],
  ['nn', 'no'],
]);

function languageFromNavigator(
  policy: LocalizationPolicy,
  navigatorLanguages: readonly string[],
): SupportedLanguage | undefined {
  for (const requested of navigatorLanguages) {
    if (typeof requested !== 'string') continue;
    const normalized = requested.trim().toLowerCase();
    if (!normalized) continue;
    const subtag = normalized.split('-')[0];
    const base = BROWSER_SUBTAG_ALIASES.get(subtag) ?? subtag;
    const exact = policy.languages.find((option) => option.code.toLowerCase() === normalized);
    if (exact) return exact.code;
    const baseMatch = policy.languages.find(
      (option) => option.code.toLowerCase().split('-')[0] === base,
    );
    if (baseMatch) return baseMatch.code;
  }
  return undefined;
}

/** The part of an element `markWidgetLanguage` touches. */
export interface LanguageMarkable {
  setAttribute: (name: string, value: string) => void;
  shadowRoot?: {
    querySelector: (selector: string) => Pick<LanguageMarkable, 'setAttribute'> | null;
  } | null;
}

/**
 * Say which language the widget's text is in. The widget otherwise inherits the
 * host page's `lang`, and the browser picks letter-casing and a screen-reader
 * voice from that: under an English page, CSS `text-transform: uppercase` turns
 * Turkish "Dinliyorum" into "DINLIYORUM" (it is "DİNLİYORUM"). Set on the host
 * and on the mount inside its shadow root, which older engines do not let
 * inherit across the boundary.
 */
export const markWidgetLanguage = (
  host: LanguageMarkable | null,
  language: SupportedLanguage,
): void => {
  if (!host) {
    return;
  }
  host.setAttribute('lang', language);
  host.shadowRoot?.querySelector('.floe-sdk-shadow-mount')?.setAttribute('lang', language);
};

/** Saved explicit choice → browser preference → site default. */
export function resolveInitialLanguage(args: {
  policy: LocalizationPolicy;
  storage?: StorageLike;
  storageKey: string;
  navigatorLanguages?: readonly string[];
}): SupportedLanguage {
  const { policy, storage, storageKey, navigatorLanguages = [] } = args;
  const saved = safeRead(storage, storageKey);
  if (saved) {
    const match = policy.languages.find((option) => option.code === saved);
    if (match) return match.code;
    // The operator may have disabled a language since the last visit. Leaving
    // the stale value around would keep retrying an invalid preference forever.
    safeRemove(storage, storageKey);
  }

  return languageFromNavigator(policy, navigatorLanguages) ?? policy.defaultLanguage;
}

export function persistLanguagePreference(
  policy: LocalizationPolicy,
  storage: StorageLike | undefined,
  storageKey: string,
  language: unknown,
): boolean {
  const allowed = policy.languages.some((option) => option.code === language);
  if (!allowed || !storage) {
    if (!allowed) safeRemove(storage, storageKey);
    return false;
  }
  try {
    storage.setItem(storageKey, language as string);
    return true;
  } catch {
    return false;
  }
}

export function beginLanguageSwitch(
  language: SupportedLanguage,
  requestId: string,
): LanguageSwitchState {
  return { pending: { language, requestId }, error: null };
}

/**
 * Release a timed-out selector without losing the id needed to reconcile an
 * eventual matching server acknowledgement. The caller's timer supplies the
 * id, so an old timer cannot expire a newer request.
 */
export function expireLanguageSwitch(
  state: LanguageSwitchState,
  requestId: string,
  errorMessage: string,
): LanguageSwitchResult {
  if (!state.pending || state.pending.requestId !== requestId) return { state };
  return {
    state: {
      pending: null,
      error: errorMessage,
      recoverableRequestId: requestId,
    },
  };
}

function allowedLanguage(
  policy: LocalizationPolicy,
  raw: unknown,
): SupportedLanguage | undefined {
  if (!isSupportedLanguage(raw)) return undefined;
  return policy.languages.some((option) => option.code === raw) ? raw : undefined;
}

/**
 * Matching acknowledgements commit a user request. A requestless event (or one
 * explicitly sourced from detection) is server auto-detection and authoritative.
 * A mismatched user-request id is stale and ignored.
 */
export function applyLanguageChanged(
  state: LanguageSwitchState,
  payload: LanguageChangedPayload,
  policy: LocalizationPolicy,
): LanguageSwitchResult {
  const language = allowedLanguage(policy, payload.language);
  if (!language) return { state };

  const requestId = typeof payload.requestId === 'string' ? payload.requestId : undefined;
  const serverDetected = payload.source === 'detected';
  if (
    (state.pending && requestId && requestId !== state.pending.requestId && !serverDetected) ||
    (!state.pending &&
      requestId &&
      requestId !== state.recoverableRequestId &&
      !serverDetected)
  ) {
    return { state };
  }

  return {
    state: {
      pending: null,
      error: null,
      ...(state.pending && !requestId
        ? { recoverableRequestId: state.pending.requestId }
        : {}),
    },
    commitLanguage: language,
  };
}

/**
 * Failure never changes the committed language. A pending UI request is
 * released only by its exact request id; a requestless detector failure cannot
 * accidentally unlock it. When no UI request is pending, a requestless failure
 * may still populate the retryable error surface.
 */
export function applyLanguageChangeFailed(
  state: LanguageSwitchState,
  payload: LanguageChangeFailedPayload,
  errorMessage: string,
): LanguageSwitchResult {
  const requestId = typeof payload.requestId === 'string' ? payload.requestId : undefined;

  if (state.pending) {
    if (!requestId || requestId !== state.pending.requestId) return { state };
    return {
      state: { pending: null, error: errorMessage },
    };
  }

  // A request id with no active pending transaction is late/stale (a timeout
  // has already surfaced its own error). Recognized requestless voice failures
  // remain useful to an idle visitor as a retryable, localized error; malformed
  // uncorrelated failures are ignored.
  if (requestId) return { state };
  if (payload.source !== 'detected' && payload.source !== 'spoken_request') {
    return { state };
  }

  return {
    state: { pending: null, error: errorMessage },
  };
}
